import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Grid,
  Pagination,
  Autocomplete,
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  SwapHoriz as StatusIcon,
  Visibility as ViewIcon,
  PhotoCamera as ImageIcon,
  AttachFile as DocumentIcon,
  Person as AssignIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  DateRange as DateIcon,
  Schedule as TimeIcon,
  TaskAlt as TaskIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {UserService} from '../../../../services/openapi/services/UserService';
import type {TaskChangeLog} from '../../../../services/openapi/models/TaskChangeLog';
import type {newUser_return} from '../../../../services/openapi/models/newUser_return';

interface ChangelogEntry extends TaskChangeLog {
  id: number;
  task_title: string;
  changed_by_name: string;
  event_type: string;
  field_name?: string;
}

interface TaskOption {
  id: number;
  title: string;
  status: string;
}

const eventTypeLabels: {[key: string]: string} = {
  TASK_CREATED: 'Task erstellt',
  TASK_UPDATED: 'Task bearbeitet',
  TASK_DELETED: 'Task gelöscht',
  TASK_ARCHIVED: 'Task archiviert',
  STATUS_CHANGED: 'Status geändert',
  TASK_OPENED: 'Task angesehen',
  IMAGE_UPLOADED: 'Bild hochgeladen',
  IMAGE_DELETED: 'Bild gelöscht',
  DOCUMENT_UPLOADED: 'Dokument hochgeladen',
  DOCUMENT_DELETED: 'Dokument gelöscht',
  ASSIGNMENT_CHANGED: 'Zuweisung geändert',
  TITLE_CHANGED: 'Titel geändert',
  DESCRIPTION_CHANGED: 'Beschreibung geändert',
  DUE_DATE_CHANGED: 'Fälligkeitsdatum geändert',
  WORK_TIME_CHANGED: 'Arbeitszeit geändert',
};

const getEventTypeColor = (eventType: string) => {
  switch (eventType) {
    case 'TASK_CREATED':
      return 'default';
    case 'IMAGE_DELETED':
    case 'DOCUMENT_DELETED':
      return 'default';
    case 'STATUS_CHANGED':
      return 'default';
    case 'TASK_OPENED':
      return 'default';
    case 'IMAGE_UPLOADED':
    case 'DOCUMENT_UPLOADED':
      return 'default';
    case 'ASSIGNMENT_CHANGED':
      return 'default';
    case 'TITLE_CHANGED':
    case 'DESCRIPTION_CHANGED':
    case 'DUE_DATE_CHANGED':
    case 'WORK_TIME_CHANGED':
      return 'default';
    default:
      return 'default';
  }
};

const getEventTypeIcon = (eventType: string) => {
  switch (eventType) {
    case 'TASK_CREATED':
      return <AddIcon color='primary' />;
    case 'STATUS_CHANGED':
      return <StatusIcon color='primary' />;
    case 'TASK_OPENED':
      return <ViewIcon color='primary' />;
    case 'TASK_ARCHIVED':
      return <ArchiveIcon color='primary' />;
    case 'IMAGE_UPLOADED':
      return <ImageIcon color='primary' />;
    case 'IMAGE_DELETED':
      return <ArchiveIcon color='primary' />;
    case 'DOCUMENT_UPLOADED':
      return <DocumentIcon color='primary' />;
    case 'DOCUMENT_DELETED':
      return <ArchiveIcon color='primary' />;
    case 'ASSIGNMENT_CHANGED':
      return <AssignIcon color='primary' />;
    case 'TITLE_CHANGED':
      return <TitleIcon color='primary' />;
    case 'DESCRIPTION_CHANGED':
      return <DescriptionIcon color='primary' />;
    case 'DUE_DATE_CHANGED':
      return <DateIcon color='primary' />;
    case 'WORK_TIME_CHANGED':
      return <TimeIcon color='primary' />;
    default:
      return <TaskIcon color='primary' />;
  }
};

export default function TaskChangelog() {
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterEventType, setFilterEventType] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<TaskOption | null>(null);
  const [availableTasks, setAvailableTasks] = useState<TaskOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [userCache, setUserCache] = useState<{[key: string]: string}>({});
  const [allUsers, setAllUsers] = useState<newUser_return[]>([]);

  const itemsPerPage = 20;

  const loadUsers = async () => {
    try {
      const users = await UserService.listUsersUserListGet();
      const usersWithMetadata = await UserService.listUsersWithMetadataUserListWithMetadataGet();
      
      console.log('Users loaded:', users);
      console.log('Users with metadata:', usersWithMetadata);
      
      setAllUsers(users);
      
      const initialCache: {[key: string]: string} = {};
      
      users.forEach(user => {
        initialCache[user.email] = user.fullname || user.email;
      });
      
      if (Array.isArray(usersWithMetadata)) {
        usersWithMetadata.forEach((userMeta: any) => {
          if (userMeta.user_id && userMeta.fullname) {
            initialCache[userMeta.user_id] = userMeta.fullname;
          } else if (userMeta.id && userMeta.fullname) {
            initialCache[userMeta.id] = userMeta.fullname;
          }
        });
      }
      
      setUserCache(initialCache);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const resolveUserName = (userId: string): string => {
    if (!userId) return 'Unbekannt';
    
    if (userCache[userId]) {
      return userCache[userId];
    }
    
    if (userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      const user = allUsers.find(u => u.email === userId);
      if (user) {
        return user.fullname || user.email;
      }
      
      return `Benutzer (${userId.substring(0, 8)}...)`;
    }
    
    return userId;
  };

  const loadTasks = async () => {
    setTasksLoading(true);
    try {
      console.log('Loading all tasks using TasksService...');

      const data = await TasksService.getTasksTaskGet(0, 1000, undefined, undefined, undefined, true);
      console.log('Tasks data received (including archived):', data);
      
      const taskOptions: TaskOption[] = data.map((task: any) => ({
        id: task.id,
        title: task.title,
        status: task.status
      }));
      
      console.log('Converted task options (including archived):', taskOptions);
      setAvailableTasks(taskOptions);
    } catch (err) {
      console.error('Fehler beim Laden der Tasks:', err);
      setError('Verbindungsfehler beim Laden der Tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const loadChangelog = async (page: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * itemsPerPage;
      const taskId = selectedTask ? selectedTask.id : undefined;

      console.log('Loading changelog with TasksService:', { skip, limit: itemsPerPage, taskId });

      const data = await TasksService.getAllChangelogEntriesTaskChangelogAllGet(
        skip,
        itemsPerPage,
        taskId
      );

      console.log('Changelog data received:', data);
      
      const changelogData = data as ChangelogEntry[];
      
      console.log('Changelog data (all entries):', changelogData);
      setChangelog(changelogData);

      if (changelogData.length < itemsPerPage) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1); 
      }
    } catch (err) {
      console.error('Fehler beim Laden des Changelogs:', err);
      setError('Verbindungsfehler beim Laden der Changelog-Einträge');
    } finally {
      setLoading(false);
    }
  };

  const createUserFriendlyDescription = (entry: ChangelogEntry) => {
    const formatValue = (value: string | null | undefined): string => {
      if (!value) return 'Nicht gesetzt';

      const statusTranslations: {[key: string]: string} = {
        OPEN: 'Offen',
        IN_PROGRESS: 'In Bearbeitung',
        COMPLETED: 'Abgeschlossen',
        BLOCKED: 'Blockiert',
        'TaskStatus.OPEN': 'Offen',
        'TaskStatus.IN_PROGRESS': 'In Bearbeitung',
        'TaskStatus.COMPLETED': 'Abgeschlossen',
        'TaskStatus.BLOCKED': 'Blockiert',
      };

      if (statusTranslations[value]) {
        return statusTranslations[value];
      }

      if (value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        return resolveUserName(value);
      }

      if (value.includes('T') && value.includes(':')) {
        try {
          return new Date(value).toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        } catch {
          return value;
        }
      }

      if (value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        try {
          return new Date(value).toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        } catch {
          return value;
        }
      }

      return value;
    };

    const formatTimeRange = (from: string | null, to: string | null): string => {
      if (!from && !to) return 'Keine Arbeitszeit';
      if (!from) return `Bis ${formatValue(to)}`;
      if (!to) return `Ab ${formatValue(from)}`;
      return `Von ${formatValue(from)} bis ${formatValue(to)}`;
    };

    switch (entry.event_type) {
      case 'TASK_CREATED':
        return `Hat eine neue Aufgabe erstellt`;

      case 'TASK_OPENED':
        return `Hat die Aufgabe angesehen`;

      case 'STATUS_CHANGED':
        if (entry.old_value && entry.new_value) {
          return `Status geändert von '${formatValue(entry.old_value)}' zu '${formatValue(
            entry.new_value
          )}'`;
        } else if (entry.new_value) {
          return `Status gesetzt auf '${formatValue(entry.new_value)}'`;
        }
        return `Hat den Status geändert`;

      case 'ASSIGNMENT_CHANGED':
        if (entry.old_value && entry.new_value) {
          const oldUser = formatValue(entry.old_value);
          const newUser = formatValue(entry.new_value);
          return `Zuweisung geändert von '${oldUser}' zu '${newUser}'`;
        } else if (entry.new_value) {
          const newUser = formatValue(entry.new_value);
          return `Aufgabe zugewiesen an '${newUser}'`;
        } else if (entry.old_value) {
          const oldUser = formatValue(entry.old_value);
          return `Zuweisung entfernt (war: '${oldUser}')`;
        }
        return `Hat die Zuweisung geändert`;

      case 'TITLE_CHANGED':
        if (entry.old_value && entry.new_value) {
          return `Titel geändert von '${entry.old_value}' zu '${entry.new_value}'`;
        } else if (entry.new_value) {
          return `Titel gesetzt auf '${entry.new_value}'`;
        }
        return `Hat den Titel geändert`;

      case 'DESCRIPTION_CHANGED':
        if (entry.old_value && entry.new_value) {
          const oldPreview = entry.old_value.length > 50 ? entry.old_value.substring(0, 50) + '...' : entry.old_value;
          const newPreview = entry.new_value.length > 50 ? entry.new_value.substring(0, 50) + '...' : entry.new_value;
          return `Beschreibung geändert von '${oldPreview}' zu '${newPreview}'`;
        } else if (entry.new_value) {
          const preview = entry.new_value.length > 50 ? entry.new_value.substring(0, 50) + '...' : entry.new_value;
          return `Beschreibung hinzugefügt: '${preview}'`;
        } else if (entry.old_value) {
          return `Beschreibung entfernt`;
        }
        return `Hat die Beschreibung bearbeitet`;

      case 'DUE_DATE_CHANGED':
        if (entry.old_value && entry.new_value) {
          return `Fälligkeitsdatum geändert von '${formatValue(entry.old_value)}' zu '${formatValue(
            entry.new_value
          )}'`;
        } else if (entry.new_value) {
          return `Fälligkeitsdatum gesetzt auf '${formatValue(entry.new_value)}'`;
        } else if (entry.old_value) {
          return `Fälligkeitsdatum entfernt (war: '${formatValue(entry.old_value)}')`;
        }
        return `Hat das Fälligkeitsdatum geändert`;

      case 'WORK_TIME_CHANGED':
        if (entry.change_details) {
          try {
            const details = JSON.parse(entry.change_details);
            if (details.from || details.to) {
              const oldRange = formatTimeRange(details.old_from, details.old_to);
              const newRange = formatTimeRange(details.from, details.to);
              return `Arbeitszeit geändert von '${oldRange}' zu '${newRange}'`;
            }
          } catch {
          }
        }
        
        if (entry.old_value && entry.new_value) {
          return `Arbeitszeit geändert von '${formatValue(entry.old_value)}' zu '${formatValue(entry.new_value)}'`;
        } else if (entry.new_value) {
          return `Arbeitszeit gesetzt auf '${formatValue(entry.new_value)}'`;
        } else if (entry.old_value) {
          return `Arbeitszeit entfernt (war: '${formatValue(entry.old_value)}')`;
        }
        return `Hat die Arbeitszeit aktualisiert`;

      case 'IMAGE_UPLOADED':
        if (entry.change_details) {
          return `Hat ein Bild hochgeladen: ${entry.change_details}`;
        }
        return `Hat ein Bild hochgeladen`;

      case 'IMAGE_DELETED':
        if (entry.change_details) {
          return `Hat ein Bild gelöscht: ${entry.change_details}`;
        }
        return `Hat ein Bild gelöscht`;

      case 'DOCUMENT_UPLOADED':
        if (entry.change_details) {
          return `Hat ein Dokument hochgeladen: ${entry.change_details}`;
        }
        return `Hat ein Dokument hochgeladen`;

      case 'DOCUMENT_DELETED':
        if (entry.change_details) {
          return `Hat ein Dokument gelöscht: ${entry.change_details}`;
        }
        return `Hat ein Dokument gelöscht`;

      case 'TASK_ARCHIVED':
        if (entry.change_details) {
          return `Hat die Aufgabe archiviert: ${entry.change_details}`;
        }
        return `Hat die Aufgabe archiviert`;

      default:
        return entry.change_details || 'Hat eine Änderung vorgenommen';
    }
  };

  useEffect(() => {
    loadUsers();
    loadTasks();
    loadChangelog(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadChangelog(1);
  }, [selectedTask, filterEventType]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    loadChangelog(page);
  };

  const filteredChangelog = changelog.filter((entry) => {
    const matchesEventType = filterEventType === 'all' || entry.event_type === filterEventType;
    return matchesEventType;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{p: 2}}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={4}>
        <Box display='flex' alignItems='center' gap={2}></Box>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box 
          display='flex' 
          alignItems='center' 
          gap={2} 
          mb={3}
          pb={2}
          borderBottom='1px solid'
          borderColor='divider'
        >
          <Typography variant='h5' fontWeight='700' color='text.primary'>
            Filter & Suche
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems='stretch'>
          <Grid item xs={12} md={4}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <FormControl fullWidth size='medium'>
                <InputLabel sx={{ fontWeight: 600 }}>Event-Typ</InputLabel>
                <Select
                  value={filterEventType}
                  label='Event-Typ'
                  onChange={(e) => setFilterEventType(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <MenuItem value='all'>
                    <Box display='flex' alignItems='center' gap={1.5}>
                      <TaskIcon color='primary' />
                      <Typography fontWeight={500}>Alle Events</Typography>
                    </Box>
                  </MenuItem>
                  {Object.entries(eventTypeLabels)
                    .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
                    .map(([type, label]) => (
                    <MenuItem key={type} value={type}>
                      <Box display='flex' alignItems='center' gap={1.5}>
                        {getEventTypeIcon(type)}
                        <Typography fontWeight={500}>{label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    loadTasks();
                    loadChangelog(currentPage);
                  }}
                  disabled={loading}
                  fullWidth
                  size='large'
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5
                  }}
                >
                  Aktualisieren
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Task Selection */}
          <Grid item xs={12} md={5}>
            <Box sx={{ height: '100%' }}>
              <Autocomplete
                options={availableTasks.sort((a, b) => a.title.localeCompare(b.title))}
                getOptionLabel={(option) => option.title}
                value={selectedTask}
                onChange={(event, newValue) => setSelectedTask(newValue)}
                loading={tasksLoading}
                clearOnEscape
                clearText='Auswahl löschen'
                noOptionsText={tasksLoading ? 'Lade Tasks...' : 'Keine Tasks gefunden'}
                size='medium'
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Task auswählen'
                    placeholder='Alle Tasks anzeigen'
                    variant='outlined'
                    helperText={`${availableTasks.length} Tasks verfügbar (inkl. archivierte)`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 600
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {tasksLoading ? <CircularProgress color='inherit' size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      startAdornment: selectedTask && (
                        <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                          <Chip
                            label={selectedTask.status === 'OPEN' ? 'Offen' : 
                                  selectedTask.status === 'IN_PROGRESS' ? 'In Bearbeitung' : 
                                  selectedTask.status === 'COMPLETED' ? 'Abgeschlossen' : 
                                  selectedTask.status === 'BLOCKED' ? 'Blockiert' :
                                  selectedTask.status === 'ARCHIVED' ? 'Archiviert' : selectedTask.status}
                            size="small"
                            color={selectedTask.status === 'COMPLETED' ? 'success' : 
                                   selectedTask.status === 'IN_PROGRESS' ? 'primary' : 
                                   selectedTask.status === 'BLOCKED' ? 'error' : 
                                   selectedTask.status === 'ARCHIVED' ? 'default' : 'warning'}
                            variant="filled"
                            sx={{ 
                              fontSize: '0.7rem',
                              height: '18px',
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </Box>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props} sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant='body2' fontWeight='600'>
                        {option.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={option.status === 'OPEN' ? 'Offen' : 
                                option.status === 'IN_PROGRESS' ? 'In Bearbeitung' : 
                                option.status === 'COMPLETED' ? 'Abgeschlossen' : 
                                option.status === 'BLOCKED' ? 'Blockiert' :
                                option.status === 'ARCHIVED' ? 'Archiviert' : option.status}
                          size="small"
                          color={option.status === 'COMPLETED' ? 'success' : 
                                 option.status === 'IN_PROGRESS' ? 'primary' : 
                                 option.status === 'BLOCKED' ? 'error' : 
                                 option.status === 'ARCHIVED' ? 'default' : 'warning'}
                          variant="filled"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: '20px',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Box>
          </Grid>

          
        </Grid>
      </Paper>

      {/* Content */}
      {loading ? (
        <Paper elevation={1} sx={{p: 6, textAlign: 'center', borderRadius: 2}}>
          <CircularProgress size={48} sx={{mb: 2}} />
          <Typography variant='h6' color='text.secondary'>
            Changelog wird geladen...
          </Typography>
        </Paper>
      ) : error ? (
        <Alert
          severity='error'
          sx={{mb: 2, borderRadius: 2}}
          action={
            <Button color='inherit' size='small' onClick={() => loadChangelog(currentPage)}>
              Erneut versuchen
            </Button>
          }>
          <Typography variant='subtitle2' sx={{mb: 0.5}}>
            Fehler beim Laden
          </Typography>
          {error}
        </Alert>
      ) : filteredChangelog.length === 0 ? (
        <Paper elevation={1} sx={{p: 6, textAlign: 'center', borderRadius: 2}}>
          <HistoryIcon sx={{fontSize: 64, color: 'text.disabled', mb: 2}} />
          <Typography variant='h6' color='text.secondary' sx={{mb: 1}}>
            {filterEventType === 'all' && !selectedTask
              ? 'Noch keine Ereignisse vorhanden'
              : 'Keine Ereignisse gefunden'}
          </Typography>
          <Typography variant='body2' color='text.disabled'>
            {filterEventType === 'all' && !selectedTask
              ? 'Sobald Änderungen an Aufgaben vorgenommen werden, erscheinen sie hier.'
              : 'Versuchen Sie es mit anderen Filterkriterien.'}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          <List sx={{p: 0}}>
            {filteredChangelog.map((entry, index) => (
              <React.Fragment key={entry.id}>
                <ListItem sx={{py: 3, px: 3, flexDirection: 'column', alignItems: 'stretch'}}>
                  {/* Header */}
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    width='100%'
                    mb={2}>
                    <Box display='flex' alignItems='center' gap={2}>
                      <Box>
                        <Typography variant='subtitle1' fontWeight='600' color='text.primary'>
                          {entry.changed_by_name || 'Unbekannt'}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
                          Task #{entry.task_id}: {entry.task_title || 'Unbenannte Task'}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant='caption' color='text.secondary' sx={{textAlign: 'right'}}>
                      {formatDate(entry.changed_at)}
                    </Typography>
                  </Box>

                  {/* Event Details */}
                  <Box display='flex' alignItems='flex-start' gap={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        mt: 0.5,
                      }}>
                      {getEventTypeIcon(entry.event_type)}
                    </Box>
                    <Box flex={1}>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        sx={{mb: 1, fontWeight: 500}}>
                        {createUserFriendlyDescription(entry)}
                      </Typography>
                      <Chip
                        label={eventTypeLabels[entry.event_type] || entry.event_type}
                        size='small'
                        color={getEventTypeColor(entry.event_type) as any}
                        variant='outlined'
                        sx={{fontSize: '0.75rem'}}
                      />
                    </Box>
                  </Box>
                </ListItem>
                {index < filteredChangelog.length - 1 && <Divider sx={{mx: 3}} />}
              </React.Fragment>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display='flex' justifyContent='center' p={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color='primary'
                size='large'
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
